import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import type { TTableCellContext as CellContext } from '@chocolate-soup-inc/cs-frontend-components';
import { Filters, TableInner } from '@chocolate-soup-inc/cs-frontend-components';
import { useCallback, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { TablePage } from '../../../components/TablePage/TablePage';
import tableStyles from '../../../components/TablePage/TablePage.module.scss';
import { BRICK_COLOR_CATALOG, getOneOffEventTypeLabel, ONE_OFF_EVENT_TYPE_OPTIONS } from '../../../entities/events/oneOff';
import { TClientOneOffEvent, useQueryAllClientOneOffEvents } from '../../../entities/events/shared';
import { usePrivateCompanyContext } from '../../../routes/outlets/PrivateCompanyOutlet';
import { TEventTypes } from '../../../generated/graphql';
import styles from './OneOffEvents.module.scss';

type TOneOffEventsProps = {
  title: string;
  eventTypes: TEventTypes[];
};

const RecipientCell = ({ cell }: CellContext<TClientOneOffEvent, unknown>) => {
  const event = cell.row.original;
  return <span className={tableStyles.tableSpacePrimary}>{event.employee?.fullName || event.recipient?.fullName || '-'}</span>;
};

const BrickColorCell = ({ cell }: CellContext<TClientOneOffEvent, unknown>) => {
  const value = cell.row.original.brickColor;
  const color = value ? BRICK_COLOR_CATALOG[value] : undefined;

  if (!color) return <span>{value || '-'}</span>;

  return (
    <span className={styles.colorCell}>
      <span aria-hidden='true' className={styles.colorSwatch} style={{ backgroundColor: color.background }} />
      {color.label}
    </span>
  );
};

export const OneOffEvents = ({ title, eventTypes }: TOneOffEventsProps) => {
  const [eventType, setEventType] = useState<string>();
  const company = usePrivateCompanyContext();
  const queryVariables = useMemo(
    () => ({
      companyId: company.id,
      eventPage: 'oneOff',
      limit: 100
    }),
    [company.id]
  );
  const { data, error, loading } = useQueryAllClientOneOffEvents(queryVariables);

  const events = useMemo(() => {
    return data.filter(({ _deleted, type }) => !_deleted && eventTypes.includes(type) && (!eventType || type === eventType)).sort((a, b) => String(b.eventDate).localeCompare(String(a.eventDate)));
  }, [data, eventTypes, eventType]);

  const getRowId = useCallback((event: TClientOneOffEvent) => event.id, []);

  return (
    <>
      <TablePage dataLoading={loading} error={error} title={`${title} (${events.length})`}>
        {eventTypes.length > 1 && (
          <Filters
            filters={[
              {
                type: 'singleSelect',
                label: 'Event Type',
                options: ONE_OFF_EVENT_TYPE_OPTIONS.filter(({ value }) => eventTypes.includes(value)),
                value: eventType,
                onChange: (value) => setEventType(value == null ? undefined : String(value))
              }
            ]}
          />
        )}
        <div className={tableStyles.tableContainer}>
          <TableInner<TClientOneOffEvent>
            enableSorting={true}
            defaultSorting={{ id: 'eventDate', desc: false }}
            columns={[
              {
                header: 'Employee',
                accessorKey: 'employee.fullName',
                cell: RecipientCell
              },
              ...(!eventTypes.some((type) => [TEventTypes.CustomBricks, TEventTypes.CarePackage].includes(type))
                ? [
                    {
                      header: 'Event Type',
                      accessorKey: 'type',
                      cell: ({ cell }: CellContext<TClientOneOffEvent, unknown>) => {
                        const event = cell.row.original;
                        return <span>{getOneOffEventTypeLabel(event.type)}</span>;
                      }
                    }
                  ]
                : []),
              {
                header: 'Event Date',
                accessorKey: 'eventDate',
                cell: ({ cell }: CellContext<TClientOneOffEvent, unknown>) => <span className={tableStyles.tableSpaceTertiary}>{readableDate(cell.row.original.eventDate)}</span>
              },
              {
                header: 'Brick Quantity',
                accessorKey: 'brickQuantity',
                cell: ({ cell }: CellContext<TClientOneOffEvent, unknown>) => <span>{cell.row.original.brickQuantity ?? 1}</span>
              },
              {
                header: 'Brick Size',
                accessorKey: 'brickSize'
              },
              {
                header: 'Brick Color',
                accessorKey: 'brickColor',
                cell: BrickColorCell
              },
              {
                header: 'Printed Text',
                accessorKey: 'brickText',
                cell: ({ cell }: CellContext<TClientOneOffEvent, unknown>) => <span className={styles.brickText}>{cell.row.original.brickText || '-'}</span>
              },
              {
                header: 'Notes',
                accessorKey: 'notes',
                cell: ({ cell }: CellContext<TClientOneOffEvent, unknown>) => <span className={styles.notes}>{cell.row.original.notes || '-'}</span>
              }
            ]}
            data={events}
            emptyText='There are no client-created one-off events yet.'
            expandable={false}
            fixedHeader={true}
            getRowId={getRowId}
            hoverableRows={false}
            virtual={false}
          />
        </div>
      </TablePage>
      <Outlet />
    </>
  );
};
